import { supabase } from './supabase';

/**
 * Check if a user has push notifications enabled for a specific type.
 * Returns true if the push should be sent, false if it should be skipped.
 */
async function shouldSendPush(targetUserId, pushType) {
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('push_enabled, ' + pushType)
      .eq('user_id', targetUserId)
      .single();

    if (error || !data) return true; // Default to sending if no prefs row
    if (!data.push_enabled) return false;
    return data[pushType] !== false;
  } catch {
    return true; // Default to sending on error
  }
}

/**
 * Send a push notification to a specific user by their OneSignal Player ID.
 * Uses the OneSignal REST API via a Supabase Edge Function to keep the API key server-side.
 *
 * @param {string} targetUserId - The Supabase user ID to notify
 * @param {object} notification - { title, message, data }
 * @param {string} pushType - The notification_preferences column to check (e.g. 'push_new_follower')
 */
export async function sendPushNotification(targetUserId, { title, message, data = {} }, pushType) {
  try {
    // Check user's notification preferences
    if (pushType) {
      const allowed = await shouldSendPush(targetUserId, pushType);
      if (!allowed) return;
    }

    // Look up the target user's player ID from their profile
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('onesignal_player_id')
      .eq('id', targetUserId)
      .single();

    if (error || !profile?.onesignal_player_id) return;

    // Send via Supabase Edge Function (keeps REST API key server-side)
    const { error: fnError } = await supabase.functions.invoke('send-push-notification', {
      body: {
        player_id: profile.onesignal_player_id,
        title,
        message,
        data
      }
    });

    if (fnError) {
      console.error('[Push] Edge function error:', fnError);
    }
  } catch (err) {
    console.error('[Push] Error sending notification:', err);
  }
}

/**
 * Send a "new follower" push notification.
 */
export async function notifyNewFollower(targetUserId, followerDisplayName) {
  await sendPushNotification(targetUserId, {
    title: 'New Follower',
    message: `${followerDisplayName} started following you`,
    data: { type: 'new_follower' }
  }, 'push_new_follower');
}

/**
 * Send a "new comment on bar you rated" push notification.
 */
export async function notifyBarComment(targetUserId, commenterDisplayName, barName) {
  await sendPushNotification(targetUserId, {
    title: 'New Comment',
    message: `${commenterDisplayName} commented on ${barName}`,
    data: { type: 'bar_comment', bar_name: barName }
  }, 'push_bar_comment');
}

/**
 * Send a "friend rated a bar" push notification.
 */
export async function notifyFriendRating(targetUserId, raterDisplayName, barName) {
  await sendPushNotification(targetUserId, {
    title: 'New Rating',
    message: `${raterDisplayName} rated ${barName}`,
    data: { type: 'friend_rating', bar_name: barName }
  }, 'push_friend_rating');
}

/**
 * Send a "friend request" push notification.
 */
export async function notifyFriendRequest(targetUserId, fromDisplayName) {
  await sendPushNotification(targetUserId, {
    title: 'Follow Request',
    message: `${fromDisplayName} requested to follow you`,
    data: { type: 'friend_request' }
  }, 'push_friend_request');
}

/**
 * Send a "rating comment" push notification.
 */
export async function notifyRatingComment(targetUserId, commenterDisplayName) {
  await sendPushNotification(targetUserId, {
    title: 'New Comment',
    message: `${commenterDisplayName} commented on your rating`,
    data: { type: 'rating_comment' }
  }, 'push_rating_comment');
}

/**
 * Send a "bar request approved" push notification.
 */
export async function notifyBarRequestApproved(targetUserId, barName) {
  await sendPushNotification(targetUserId, {
    title: 'Bar Approved',
    message: `Your bar request for ${barName} was approved`,
    data: { type: 'bar_request_approved', bar_name: barName }
  }, 'push_bar_request_approved');
}

/**
 * Send a "bar request rejected" push notification.
 */
export async function notifyBarRequestRejected(targetUserId, barName) {
  await sendPushNotification(targetUserId, {
    title: 'Bar Request Update',
    message: `Your bar request for ${barName} was rejected`,
    data: { type: 'bar_request_rejected', bar_name: barName }
  }, 'push_bar_request_rejected');
}

export async function notifyCheckIn(targetUserId, fromDisplayName, barName, messageText) {
  await sendPushNotification(targetUserId, {
    title: `${fromDisplayName} checked in`,
    message: messageText || `${fromDisplayName} is at ${barName}`,
    data: { type: 'check_in', bar_name: barName }
  }, 'push_check_in');
}
